import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import TemplateFolder from "@/services/template/template-folder";
import type { GetTemplateFolder } from "@/dtos/TemplateFolder.dto";

export interface TemplateFoldersParams {
  organizationId: string;
  parentTemplateFolderId?: string;
  createdById?: string;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

export function useTemplateFolders(
  params: Ref<TemplateFoldersParams | null> | TemplateFoldersParams | null
) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["templateFolders", "disabled"];
    return [
      "templateFolders",
      p.organizationId,
      p.parentTemplateFolderId || "",
      p.createdById || "",
    ];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return p !== null && p !== undefined && !!p.organizationId;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const queryParams: GetTemplateFolder & Record<string, any> = {
        organizationId: p.organizationId,
      };

      if (p.parentTemplateFolderId) {
        queryParams.parentTemplateFolderId = p.parentTemplateFolderId;
      }

      if (p.createdById !== undefined) {
        queryParams.createdById = p.createdById;
      }

      const result = await TemplateFolder.getTemplateFolders(queryParams);

      if (result.data.status) {
        return result.data.data;
      }

      throw new Error(result.data.msg || "Failed to load template folders");
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
